// © Microsoft Corporation. All rights reserved.

import { ActionMap, CommonActions, DataConnector } from "@msnews/experiences-redux";
import {
    AppError,
    ExperienceInitialPositionProps,
    ExperienceInitialProps
} from "@msnews/experiences-base";
import {
    BaseExperienceConfig,
    ChildArrangementMetadata,
    ChildExperienceConfigInfo,
    ChildExperiencesDeferRenderType,
    ExperienceState,
    IRequestContext,
    appEnvironment,
    getConfigId,
    getExperienceType,
    resolver,
    tryAddOrderedTelemetryChild,
    tryStringifyJson
} from "@msnews/core";
import { ChildWithInvalidExperience, ComponentConnectorAppErrors, LoadDataConnectorConfigFailed, LoadDataConnectorFailed, createError, logger, stringifyError } from "@msnews/diagnostics";
import { ConnectorNamespaces, ExperienceIds } from "@msnews/experiences-constants";
import { DeferralEventType, ExperienceMilestoneEventTypes, MilestoneDataActions, MilestoneDataConnector, UpdateTimingSignature, createTimingUpdate, getExperienceLoadDelegate, getExperienceRenderDelegate, pageRevealStartedObserver, requestPageRevealCompletedAsync, requestPageRevealStartedAsync, shouldTrackExperience } from "@msnews/milestone-data";
import { Dispatch, Store } from "redux";
import { DynamicReducer, dataConnectorInjector } from "@msnews/experiences-dynamic-reducer";
import { IActionPayload, IReducer } from "@msnews/experiences-redux-contracts";
import { ObjectTelemetryContract, Telemetry, TelemetryContext, TelemetryObjectBase, TelemetryTags, TelemetryType } from "@msnews/telemetry-contracts";
import { PageInvalidationReasons, registerStaleRevealReact, trackExpWithPersistentDataUpdate, trackPageInvalidation } from "@msnews/page-lifecycle-helpers";
import { TelemetryObject, trackComponent } from "@msnews/telemetry-core";
import { canUseDOM, getLocationHref, getLocationSearch } from "@msnews/isomorphic-util";

import { ConfigProcessor } from "@msnews/config-validator";
import { ExperienceLoadInfo } from "./ExperienceLoadInfo";
import { ExperienceLoader } from "../experience-loader/ExperienceLoader";
import { IComponentConnector } from "./IComponentConnector";
import { PageBase } from "@msnews/experiences-page-base";
import React from "react";
import { connect } from "react-redux";
import { getMilestoneDataConnector } from "@msnews/milestone-data";
import { instrumentPerf } from "./ExperiencePerfTimeline";
import { isEmpty } from "lodash-es";
import { isFunction } from "util";
import { networkErrorString } from "./ComponentConnector.constants";
import { subspaced } from "react-redux-subspace";

export type mapToPropsSignature<TState, TProps> = (state: TState, ownProps?: TProps) => TProps;
export type mapStateToPropsReturnType<TState, TProps> = TProps | mapToPropsSignature<TState, TProps>;
export type mapDispatchToPropsReturnType<TState, TProps> = TProps | mapToPropsSignature<TState, TProps>;

/**
 * Map for making config-less data connectors faster to load
 *
 * HACK: HACK: HACK: This is a temporary hack until we can update CMS configs to remove
 *  the HREF requirement for config-less connectors, so that we can opt out
 *  of loading configs without having this special check. Need to do in phases
 *  so that we're not breaking PROD users with old scripts.
 */
export const ConfiglessConnectors = {
    [ConnectorNamespaces.BingImageData]: true,
    [ConnectorNamespaces.TelemetryData]: true,
    [ConnectorNamespaces.TopSitesLayoutData]: true,
    [ConnectorNamespaces.PageClickTrackerData]: true,
    [ConnectorNamespaces.OfficeSharedData]: true,
    [ExperienceIds.pivotContentV2]: true
};

/**
 * Base class for a component connector. Every connected component in an app has a connector that inherits from this class
 * @class
 */
export abstract class ComponentConnector<TConfig, TState, TProps, TParentState>
    extends DataConnector<TConfig, TState, TParentState>
    implements IComponentConnector<TState, TProps, TParentState> {

    /**
     * A map of child connectors. The key to the map is a concatenation: ExperienceType_ExperienceInstanceId.
     * The ExperienceType is the the same as the ExperienceId in the @msnews/experiences-constants ExperienceIds enum (i.e. River)
     * The ExperienceInstanceId is set in config, as part of the parent experience config. Which children get created and rendered
     * by a parent experience will be specific to that experience, so it will be up to the parent experience to determine the
     * instanceId of the children to be rendered by looking in its config object.
     * @type Map<string, IComponentConnector<any, any, any>>
     */
    protected childMap?: Map<string, IComponentConnector<any, any, any>> = new Map();

    /**
     * Telemetry object of the component. It is automatically populated by this class when experience calls onComponentConnect().
     */
    protected telemetryObject: TelemetryObjectBase;

    /**
     * All telemetry objects of all subcomponent
     * It constitutes an object that provides access to each of the telemetry object children of a provided telemetryObject
     * attribute that is passed to its contructor. Particularly useful when there are many telemetry object children that need to be accessed
     * anywhere in the experience, for example, to update their contracts or to render them within a particular DOM element.
     * A great example of how it could be used can be found in the Feedback dialog experience:
     * FeedbackDialog.telemetry.ts: https://microsoft.visualstudio.com/ICE/_git/msnews-experiences/pullrequest/4063110?_a=files&path=%2Fexperiences%2Ffeedback-dialog%2Fsrc%2FFeedbackDialog.telemetry.ts
     * Feedbackdialog.tsx: https://microsoft.visualstudio.com/ICE/_git/msnews-experiences/pullrequest/4063110?_a=files&path=%2Fexperiences%2Ffeedback-dialog%2Fsrc%2FFeedbackDialog.tsx
     */
    protected telemetryContext: TelemetryContext = undefined;

    /**
     * The telemetry tags to pass along as unhandled props for rendering
     */
    protected telemetryTags: TelemetryTags = null;

    /**
     * The group the experience belongs to, if applicable. This property is to be set as needed in the specific experience connectors
     */
    protected $experienceGroup: string = undefined;

    /**
     * The connected rendering component for the experience connector
     */
    private $connectedComponent: React.ComponentClass<TProps> = undefined;

    /**
     * The connected rendering component ReactElement instance for the experience connector
     */
    private $componentInstance: React.ReactElement<any> = undefined;

    /**
     * The connector props used to connect this connector to an experience
     */
    private $connectorProps: ExperienceInitialProps;

    /**
     * flag indicating if we should render when offline. Default is false
     */
    private $renderIfOffline = false;

    /**
     * flag indicating if experience is liable to be stale
     */
    private $isStale = false;

    /**
     * The experience type
     */
    private $experienceType: string = undefined;

    /**
     * failsafe timeout(ms) to render child experiences based on milestone batch load strategy
     */
    private $maxWaitToBatchRenderChildExperiences = 4000;

    /**
     * The milestone data connector (if it exists)
     */
    private $milestoneDataConnector: MilestoneDataConnector = undefined;

    /**
     * Gets the connected component for this experience
     */
    public get connectedComponent(): React.ComponentClass { return this.$connectedComponent; }

    /**
     * Gets the component instance for this experience. This is used for rendering the experience
     */
    public get componentInstance(): React.ReactElement<any> { return this.$componentInstance; }

    /**
     * Sets the component instance for this experience
     */
    public set componentInstance(value: React.ReactElement<any>) { this.$componentInstance = value; }

    /**
     * Gets the experience type for this experience.
     */
    public get experienceType(): string { return this.$experienceType; }

    /**
     * Sets the renderIfOffline flag
     */
    public set renderIfOffline(value: boolean) { this.$renderIfOffline = value; }

    /**
     * Gets the staleLiable flag
     */
    public get isStale(): boolean { return this.$isStale; }

    /**
     * Gets the staleLiable flag
     */
    public set isStale(value: boolean) { this.$isStale = value; }

    /**
     * Get a map of the child experience component instances, which is appropriate for passing into the
     * inspector function as part of mapStateToProps
     */
    public get childComponentInstanceList(): { [instanceId: string]: React.ReactElement<any>; } {
        const childComponentList: { [instanceId: string]: React.ReactElement<any>; } = {};
        this.childMap.forEach((connector, key) => {
            childComponentList[key] = connector.componentInstance;
        });
        return childComponentList;
    }

    /**
     * Construct a new Component connector
     * @constructor
     * @param { string } namespace The redux subspace namespace for the connector
     * @param { string } parentNamespace The redux subspace namespace for the parent connector, if it exists
     * @param { DynamicReducer } rootReducer The root reducer for the Application
     * @param { IReducer<TState> } reducer The reducer for this connector instance
     * @param { Store<TParentState>} parentStore The parent store (or redux subspace) that this connector will be a subspace within
     * @param { TConfig } config Param containing the config for the connector
     * @param { IRequestContext } requestContext The injected request context
     */
    public constructor(
        namespace: string,
        parentNamespace: string,
        rootReducer: DynamicReducer,
        reducer: IReducer<TState>,
        parentStore: Store<TParentState>,
        config: TConfig,
        requestContext: IRequestContext
    ) {
        super(namespace, parentNamespace, rootReducer, reducer, parentStore, config, requestContext);
        this.selectComponentState = this.selectComponentState.bind(this);

        // To get the experience type, split the namespace at the first "_" and use the first substring
        // as the type. The convention for namespace is: ExperienceType_configHref_InstanceId. If there is no instanceId, then then namespace is just: ExperienceType
        const namespaceArray = namespace.split("_");
        this.$experienceType = namespaceArray[0];
        // configNamespace includes experienceType + index path
        const configNamespace = (namespaceArray.length > 1) ? this.$experienceType + namespaceArray[1] : this.$experienceType;

        this.config = ConfigProcessor.processConfig(this.config, configNamespace) as TConfig;

        // update if experience is staleable
        this.setupForPageReveal();
    }

    /**
     * Redux mapper used to map the current state to props the component use to render itself.
     * @method
     * @param {TState} state The current state of the component.
     * @param {any} ownProps The set of properties that is passed in from where the component is created.
     * This parameter should be not nil only when the component is supposed to have part of its properties coming from a parent component.
     * It can be omitted when the entire set of props is derived from the state.
     * @returns {mapStateToPropsReturnType<TState, TProps>} It can either return the set of mapped props as an object of type TProps derived from a state
     * object of type TState or a function that will be used to map the state to the props.
     */
    public abstract mapStateToProps(state: TState, ownProps?: any): mapStateToPropsReturnType<TState, TProps>;

    /**
     * Inject into props redux action creators.
     * @method
     * @param {Dispatch} dispatch The redux dispatch function.
     * @param {any} ownProps The set of properties that is passed from where the component is created.
     * This parameter should be not nil only when the component is supposed to have part of its properties coming from a parent component.
     * It can be omitted when the entire set of props is derived from the state.
     * @returns {TProps} An enriched set of props.
     */
    public mapDispatchToProps(dispatch: Dispatch<IActionPayload>, ownProps?: any): mapDispatchToPropsReturnType<TState, TProps> {
        return {} as TProps;
    }

    /**
     * Overrides the default redux mergeProps function that will add a callback function for reporting experience milestone timing
     * @method
     * @param {TState} stateProps The set of properties from mapStateToProps.
     * @param {TProps} dispatchProps The set of properties from mapDispatchToProps.
     * @param {any} ownProps The set of properties that is passed from where the component is created.
     * @returns {TProps} The merged set of props.
     */
    public mergeProps(stateProps: TProps, dispatchProps: TProps, ownProps: any): TProps {
        // WARNING WARNING WARNING. Some render engines, like preact will take dependencies on some props that may be set to undefined, so the
        // merge must preserve properties that are undefined in the source (i.e stateProps) in the result.
        // Specific to preact depends on the constructor property being explicity set to undefined. When an element is created by preact
        // the constructor property is set to undefined, and that must be preserved by this merge function.
        // Avoid using lodash merge, as it does not preserve the undefined properties

        // If we are in Mns Creator Dashboard mode, then set props for the config href, and experience type. These will get rendered
        // on each experience as unhandled props
        let mnsDashBoardProps = {};
        if (appEnvironment.ShowMnsCreatorDashboard) {
            mnsDashBoardProps = {
                "data-config-href": this.configHref,
                "data-experience-type": this.experienceType
            };
        }

        // The onExperienceMilestoneTimingUpdate and isStale prop is set here, so that it is injected into the resulting properties for all experiences.
        return {
            onExperienceMilestoneTimingUpdate: this.onExperienceMilestoneTimingUpdate,
            isStale: this.isStale,
            ...mnsDashBoardProps,
            ...ownProps,
            ...stateProps,
            ...dispatchProps,
            ...(this.requestContext && { requestContext: this.requestContext } || {})
        };
    }

    /**
     * Connects the passed in component to the redux store/state of the connector
     * @method
     * @param {React.ComponentType<TProps>} componentToConnect The react element to connect.
     * @param {any} connectorProps Optional connector props to pass to the onComponentConnect call.
     * @returns {React.ComponentClass<any>} The Redux connected component.
     */
    public async connectComponent(componentToConnect: React.ComponentType<TProps>, connectorProps?: ExperienceInitialProps): Promise<React.ComponentClass<TProps>> {

        // If we are not online, and we should not render offline, then just return undefined. This will prevent the experience from ever
        // rendering
        if (canUseDOM() && !navigator.onLine && !this.$renderIfOffline) {
            if (connectorProps && connectorProps.onAppErrorCallback) {
                connectorProps.onAppErrorCallback(
                    {
                        id: 404,
                        message: networkErrorString,
                        source: "ComponentConnector.ts",
                        severity: 0
                    }
                );
            }

            return undefined;
        }

        const connectMeasureEnd = this.perfTimeline && this.perfTimeline.startMeasure("connect").endMeasure;

        // Call the onComponentConnect method. This is done first, so that the connector can see the initialProps before mapStateToProps is called
        // onComponentConnect is async, so we need to wait for it to complete first
        await this.onComponentConnect(connectorProps);

        if (this.telemetryObject) {
            componentToConnect = trackComponent(componentToConnect, this.telemetryObject);
        }

        // if this experience should be instrumented for perf, do so and get the perf HOC
        if (this.perfTimeline) {
            componentToConnect = instrumentPerf(componentToConnect, this.perfTimeline);
            this.mapStateToProps = this.perfTimeline.instrumentFunc("mapStateToProps", this.mapStateToProps, this);
            this.mapDispatchToProps = this.perfTimeline.instrumentFunc("mapDispatchToProps", this.mapDispatchToProps, this);
            this.mergeProps = this.perfTimeline.instrumentFunc("mergeProps", this.mergeProps, this);
        }

        // Connect the component to redux (will cause mapStateToProps to be called)
        const connectedComponent: React.ComponentClass<any> = connect(
            this.mapStateToProps.bind(this),
            this.mapDispatchToProps.bind(this),
            this.mergeProps.bind(this),
            { pure: true }
        )(componentToConnect);

        // Wrap the redux connected component in a Subspace Provider, and set that as the connectedComponent for this connector
        this.$connectedComponent = subspaced(this.selectComponentState, this.namespace)(connectedComponent);

        if (connectMeasureEnd) {
            connectMeasureEnd();
        }

        return this.$connectedComponent;
    }

    /**
     * This method reads the experience config and adds any ordinal(index) based child experiences specified in the config to the input
     * ChildExperienceConfigInfo array at the specified ordinal position defined in config.
     * @method
     * @param {ExperienceLoadInfo[]} ExperienceLoadInfoList Array of ChildExperienceConfigInfo objects
     * @param {number} mergeStartPosition MergeStartPosition represents the start position for the merge. If your experience only need to call this once,
     * it should be 1. For deep river kind of scenario, you would need to pass the updated merge start position. StartPosition = lastEndPosition + 1
     * @returns {ExperienceLoadInfo[]}
     */
    public mergeOrdinalChildExperiences(
        experienceInfoList: ExperienceLoadInfo[],
        mergeStartPosition: number,
        callback?: (experienceInfo: ExperienceLoadInfo) => void
    ): ExperienceLoadInfo[] {

        if (!experienceInfoList) {
            return null;
        }

        if (mergeStartPosition < 1) {
            Telemetry.sendAppErrorEvent({
                ...ComponentConnectorAppErrors.ListStartPositionInvalid,
                message: "listStartFrom must be greater than 0."
            });

            return experienceInfoList;
        }

        const config = this.config as BaseExperienceConfig<any>;

        if (!config.additionalChildExperiences || config.additionalChildExperiences.length === 0) {
            return experienceInfoList;
        }

        const ordinalBlockForCurrentColumn = ConfigProcessor.getOrdinalObjectForCurrentBreakpoint<ChildArrangementMetadata>(config.additionalChildExperiences);
        let mergeEndPos = mergeStartPosition + experienceInfoList.length;

        // Following code loops through the ChildArrangementMetadata array and inserts an additional child experience if startPosition provided in config is greater than mergeStartPosition
        // and less than mergeEndPos which is mergeStartPosition + length of the input childConfigInfoList
        ordinalBlockForCurrentColumn.children.forEach((childArrangementMetadata) => {
            const childExpArrangement = childArrangementMetadata.arrangement;

            // TS requires to define this explicitly and then assign it in line 234.
            const positionProps: ExperienceInitialPositionProps = { experienceArrangement: childExpArrangement };

            if (childExpArrangement && childExpArrangement.startPosition >= mergeStartPosition && childExpArrangement.startPosition <= mergeEndPos) {
                const insertAt = childExpArrangement.startPosition - mergeStartPosition;
                const experienceInfo: ExperienceLoadInfo = {
                    configInfo: childArrangementMetadata.childExperienceConfigInfo,
                    initialProps: positionProps
                };

                experienceInfoList.splice(insertAt, 0, experienceInfo);
                mergeEndPos++;

                if (typeof callback === "function") {
                    callback(experienceInfo);
                }
            }
        });

        return experienceInfoList;
    }

    /**
     * This method is called when a component is connected to the connectors' store.
     * This method can be overridden and used by connectors to perform one time initialization functions
     * @method
     * @param {any} connectorProps Optional initial props passed into the onComponentConnect call.
     * @returns {void}
     */
    public async onComponentConnect(connectorProps?: ExperienceInitialProps): Promise<void> {

        // save the last connected connector props
        this.$connectorProps = connectorProps;

        // Setup telemetry props for this experience.
        if (connectorProps) {
            // ExperienceTelemetry is passed as an initial prop when the parent has precreated the telemetry object for this component.
            // This allows the parent to create all its child components' telemetry objects in a deterministic order, thus ensuring the
            // telemetry ordinalIDs are always generated in a deterministic manner. This is important for hydrating prerendered pages.
            this.telemetryObject = this.telemetryObject || connectorProps.experienceTelemetry;

            if (!this.telemetryObject) {
                const telemetryContract = {
                    type: TelemetryType.Module,
                    name: this.$experienceType,
                    content: {}
                } as ObjectTelemetryContract;

                if (connectorProps.parentTelemetry && !isEmpty(connectorProps.parentTelemetry)) {
                    this.telemetryObject = connectorProps.parentTelemetry.addOrUpdateChild(telemetryContract, this.telemetryObject);
                } else {
                    this.telemetryObject = new TelemetryObject(telemetryContract);
                }
            }

            // generate the telemetry tags to pass along for rendering
            this.telemetryTags = this.telemetryObject.render();

            // Override the onAppError function if passed one.
            if (isFunction(connectorProps.onAppErrorCallback)) {
                this.onAppError = connectorProps.onAppErrorCallback;
            }
        }

        // load data connectors
        // Experiences relying on looking up data connectors need await here. Data Connectors should not have any blocking calls
        // in the constructor.
        await this.loadDataConnectors();

        for (const injectedDataConnector of this.injectedDataConnectors) {
            // Setup the initial state
            CommonActions.dataConnectorMergeInit.getActionSender(this).send(injectedDataConnector);

            // Register the merge so we get updates
            dataConnectorInjector.registerInjection(this.fullNamespace, injectedDataConnector);
        }

        // load up child experiences
        await this.onLoadChildExperiences();
    }

    /**
     * Gets a new instance of a ReactElement for the connected Component for this connector, that can be used to render
     * the experience.
     * This method will replace any existing ReactElement instance that has been previously created.
     * @param ownProps optional props to be passed into the connected components' mapStateToProps method
     */
    public getNewComponentInstance(ownProps?: TProps): React.ReactElement<TProps> {
        // If there is a connected component for this connector, then create an instance of it.
        if (this.$connectedComponent) {
            const newProps = { ...ownProps, key: this.namespace };
            this.$componentInstance = React.createElement(this.$connectedComponent, newProps);
        }

        return this.$componentInstance;
    }

    /**
     * Report a milestone timing event
     * @param milestoneType The type of milestone timing is being reported for
     * @param startTime The start time (if defined)
     * @param endTime  The end time (if defined)
     */
    protected onExperienceMilestoneTimingUpdate = (
        milestoneType: ExperienceMilestoneEventTypes,
        startTime?: number,
        endTime?: number
    ) => {

        if (!shouldTrackExperience(this.experienceType)) {
            return;
        }

        // Do a onetime check to get the Milestone Data Connector instance
        if (this.$milestoneDataConnector === undefined) {
            this.$milestoneDataConnector = getMilestoneDataConnector() || null;
        }

        // Skip the Milestone update if we don't have a Milestone Data connector
        if (!this.$milestoneDataConnector) {
            return;
        }

        switch (milestoneType) {
            case ExperienceMilestoneEventTypes.domComplete:
                this.updateTiming(MilestoneDataActions.updateDomCompleteTiming, startTime, endTime);
                break;

            case ExperienceMilestoneEventTypes.initialized:
                this.updateTiming(MilestoneDataActions.updateInitTiming, startTime, endTime);
                break;

            case ExperienceMilestoneEventTypes.rendered:
                this.updateTiming(MilestoneDataActions.updateRenderTiming, startTime, endTime);
                break;

            case ExperienceMilestoneEventTypes.visuallyReady:
                this.updateTiming(MilestoneDataActions.updateVisuallyReadyTiming, startTime, endTime);
                break;

            case ExperienceMilestoneEventTypes.startLoading:
                this.updateTiming(MilestoneDataActions.updateStartLoadingTiming, startTime, endTime);
                break;

            case ExperienceMilestoneEventTypes.staleRevalidationComplete:
                MilestoneDataActions.updateExperiencePublicState.getActionSender(this.$milestoneDataConnector).send({
                    experienceType: this.$experienceType,
                    experienceGroup: this.$experienceGroup,
                    experienceInstance: this.namespace,
                    eventType: ExperienceMilestoneEventTypes.staleRevalidationComplete
                });
                break;

            default:
                // do nothing for the default cause, since there is no default timing that can be updated
                logger.log(`Milestone timing update was attempted for an unknown milestone type: ${milestoneType}`);
                break;
        }
    };

    /**
     * This method reads the experience config and returns child experiences placed using index based position.
     * @method
     * @returns {ExperienceLoadInfo[]}
     */
    protected getOrdinalChildExperiences(): ExperienceLoadInfo[] {
        const config = this.config as BaseExperienceConfig<any>;

        if (!config.additionalChildExperiences || config.additionalChildExperiences.length === 0) {
            return null;
        }

        const result: ExperienceLoadInfo[] = [];
        const ordinalBlockForCurrentColumn = ConfigProcessor.getOrdinalObjectForCurrentBreakpoint(config.additionalChildExperiences);

        ordinalBlockForCurrentColumn.children.forEach((childArrangementMetadata) => {
            const childExpArrangement = childArrangementMetadata.arrangement;

            // TS requires to define this explicitly and then assign it in line 234.
            const positionProps: ExperienceInitialPositionProps = { experienceArrangement: childExpArrangement };
            result.push({
                configInfo: childArrangementMetadata.childExperienceConfigInfo,
                initialProps: positionProps
            });
        });

        return result;
    }

    /**
     * Loads all the children experiences defined under the configuration in parallel.
     * @returns {Promise{void}[]} - an array of promises that loads all the children experiences.
     */
    protected async onLoadChildExperiences(): Promise<void> {
        // Load all the children.
        // TODO: passing in the parent's connector props to children verbatim seems wrong.
        //  However experiences such as spotlight seems to be loading it using this method by
        //  passing child specific initial props from all the way from the top level page experience,
        //  so need to resolve that before removing the this.$connectorProps parameter.
        await this.loadChildrenExperiences(ConfigProcessor.getChildrenExperienceConfigInfos(this.config, this.$connectorProps), this.$connectorProps);
    }

    /**
     * Load a list of child experiences
     * @param children - The list (array) of child experiences to be loaded
     * @param childConnectorProps - The connector props to pass to each child experience as it is loaded
     * @returns {Promise{void}} - the async operation that denotes loading of all the children.
     */
    protected async loadChildrenExperiences(children: ChildExperienceConfigInfo[], childConnectorProps?: ExperienceInitialProps): Promise<void> {
        if (!children || children.length === 0) {
            return;
        }

        const childExpsToLoadImmediately = [];

        const reRenderCallback = () => {
            this.readyExperienceToRender(children);
        };

        for (const child of children) {
            // Call tryAddOrderedTelemetryChild to ensure child telemetry is constructed in order and thus the child telemetry
            // ordinal IDs are deterministic. Otherwise ordinal IDs can differ between server side renders and client side hydration.
            const promiseDependency = this.startProcessingChildExperience(child, reRenderCallback, {
                ...childConnectorProps,
                experienceTelemetry: tryAddOrderedTelemetryChild(this.telemetryObject, getExperienceType(child))
            });
            childExpsToLoadImmediately.push(promiseDependency);
        }

        // NOTE: In order to make sure that the fail-fast behavior of Promise.all doesn't kick in, we're wrapping the individual promises
        // with a catch block and ensuring that they always resolve (never reject/throw error). If we don't do this, then
        // even if one child promise gets rejected early, then the parent experience might fail to load the remaining children.
        // childExpsToLoadImmediately.map(async childExp => await childExp());
        await Promise.all(childExpsToLoadImmediately);
    }

    /**
     * Gets the list of data connectors to inject into this experience.
     */
    protected get injectedDataConnectors(): ConnectorNamespaces[] {
        return [];
    }

    /**
     * Load a list of data connectors provided in the experience config
     * @returns {Promise{void}} - the async operation that denotes loading of all the children.
     */
    protected async loadDataConnectors(): Promise<void> {
        await Promise.all(
            ConfigProcessor.getChildrenDataConnectorConfigInfos(this.config).map(connector =>
                this.loadDataConnector(connector)));
    }

    /**
     * Loads a single child experience with the given connector initial props.
     * @param configInfo - the child experience config info
     * @param childConnectorProps - optional initial props to pass to connector.
     * @returns {Promise{void}} - the async operation that denotes loading of the child.
     */
    protected async loadChildExperience(
        configInfo: ChildExperienceConfigInfo,
        childConnectorProps?: ExperienceInitialProps
    ): Promise<void> {
        // If the experience is delay loaded, load it through the same flow as composition ones so that MilestoneData deferred loading logic is intact.
        const milestoneDependencyToLoadChildExp = getExperienceLoadDelegate(getExperienceType(configInfo));
        if (milestoneDependencyToLoadChildExp) {
            await this.loadChildrenExperiences([configInfo], childConnectorProps);
            return;
        }

        const childConnector = await this.getChildExperienceConnector(configInfo, childConnectorProps);
        if (!childConnector) {
            configInfo.state = ExperienceState.Failed;
            return;
        }

        this.addConnectorToChildMap(configInfo, childConnector);
    }

    /**
     * Provides the connector props to be passed into the onComponentConnect method for any children that are created for this
     * experience connector
     * @param childExperienceType The experience Type of the child experience being loaded
     * @param childExperienceInstanceId The experienceInstance Id of the child experience being loaded
     */
    protected getChildExperienceConnectorProps(childExperienceType: string, childExperienceInstanceId: string): ExperienceInitialProps {
        return (
            {
                parentTelemetry: this.telemetryObject,
                context: this.$connectorProps && this.$connectorProps.context,
                onAppErrorCallback: this.onAppError,
                requestContext: this.requestContext
            });
    }

    /**
     * function to call an action to reRender experience
     */
    protected reRenderExperience(): void {
        CommonActions.renderExperience.getActionSender(this).send();
    }

    /**
     * function to re-initialize experience state, re-fetch data if stale.
     */
    protected reInitExperienceState(): void {
        CommonActions.renderExperience.getActionSender(this).send();
    }

    /**
     * The optional function for Experiences to call use when an error occurs.
     * Defined as empty function so clients don't need to check it.
     * Passed to child experiences. Used mainly outside of Peregrine. e.g. Office page.
     * NOTE: we need to default to track.trackAppErrorEvent for PH components.
     */
    protected onAppError: ((error: AppError) => void) = (err) => Telemetry.sendAppErrorEvent(err);

    /**
     * Invoked when the experience is stale and needs to be updated. Override this method to refresh the experience
     * if it can get stale.
     * @returns True if the experience took an action on being stale.
     */
    protected revalidateExperienceStaleState(): boolean {
        return false;
    }

    /**
     * Revalidate the experience if needed and store outcome in isStale.
     */
    private revalidateIfNeeded = () => {
        this.isStale = this.revalidateExperienceStaleState();
        return this.isStale;
    };

    /**
     * track experience changes for pageReveal
     * @param expType - experience type to be revalidated
     */
    protected trackExperienceChangesForPageReveal(expType?: string): void {
        // track changes in persistent store to be used on pageReveal
        trackExpWithPersistentDataUpdate(expType ? expType : this.experienceType);
    }

    /**
     * track experience changes for pageReveal
     * @param invalidationReason - page invalidation reason
     */
    protected trackPageInvalidationOnReveal(invalidationReason: PageInvalidationReasons): void {
        // track changes in persistent store for page invalidation on pageReveal
        trackPageInvalidation(invalidationReason);
    }

    /**
     * Base implementation for page visible, resolves when page moves from hidden to visible.
     * This is currently used for Prerender PageReveal scenario.
     * In future this can be extended for page lifecycle api events.
     */
    protected async requestPageVisibleAsync(): Promise<void> {
        return await requestPageRevealStartedAsync();
    }

    /**
     * Base implementation for page visible complete, resolves when page is ready to be revealed
     * This is currently used for Prerender PageReveal scenario.
     * In future this can be extended for page lifecycle api events.
     */
    protected async requestPageVisibleCompleteAsync(): Promise<void> {
        return await requestPageRevealCompletedAsync();
    }

    /**
     * Initial setup for page reveal
     */
    private setupForPageReveal() {
        const experienceConfig = this.config as BaseExperienceConfig<any>;
        // mock config for staleness if url override is provided
        this.mockStalenessConfig(experienceConfig);

        if (experienceConfig && experienceConfig.stalenessInfo && experienceConfig.stalenessInfo.isStaleCheckRequired) {
            registerStaleRevealReact({ experienceType: this.$experienceType, thresholdMinutes: experienceConfig.stalenessInfo.staleThreshold, callback: this.revalidateIfNeeded });
        }
    }

    /**
     * Common helper to send a milestone timing update for a specific action.
     */
    private updateTiming(actionMap: ActionMap<UpdateTimingSignature>, startTime?: number, endTime?: number): void {
        actionMap.getActionSender(this.$milestoneDataConnector)
            .send(createTimingUpdate(this.$experienceType, this.$experienceGroup, this.namespace, startTime, endTime));
    }

    /**
     * Initializes connector and connected component for a given child experience.
     * @param configInfo - the child experience config info
     * @param childConnectorProps - optional initial props to pass to connector.
     * @returns {Promise<IComponentConnector<any,any,any>>} - the async operation that returns the connector for child experience.
     */
    private async getChildExperienceConnector(
        configInfo: ChildExperienceConfigInfo,
        childConnectorProps?: ExperienceInitialProps): Promise<IComponentConnector<any, any, any>> {

        const experienceType = getExperienceType(configInfo);

        try {
            // Get an experience loader instance for the child experience
            const loader = new ExperienceLoader(configInfo, null, this.fullNamespace, this.store, this.requestContext, experienceType);

            // Get the connector for the child experience, and add it to the child connector map
            await loader.loadConnector();
            const { connector: childConnector } = loader;

            // Get the connected component for the child experience.
            await loader.getConnectedExperience({
                // get core connector props
                ...this.getChildExperienceConnectorProps(experienceType, configInfo.instanceId),
                // add any override props
                ...(childConnectorProps || {})
            });

            // Get the initial component instance for the child. The experience connector can get a new instance if it needs to do special
            // processing (i.e. passing in OwnProps)
            childConnector.getNewComponentInstance();

            return childConnector;
        } catch (error) {
            // Remove this config from cache.
            const configId = getConfigId(configInfo);
            if (typeof configId === "string") {
                resolver.removeCachedConfig(configId);
            }
            // TODO: else Git Configs

            // On server side we need to rethrow error so client side rendering takes over, this is to prevent rendering a mismatched page
            if (!canUseDOM()) {
                throw error;
            }

            // Log an app error if the loader failed to load for whatever reason
            /// NOTE: DO NOT throw Error or reject promise here, or else the parent might not load the remaining child experiences.
            Telemetry.sendAppErrorEvent({
                ...ComponentConnectorAppErrors.BundleLoadError,
                message: `Failed to load experience ${experienceType || ""}`,
                pb: {
                    ...ComponentConnectorAppErrors.BundleLoadError.pb,
                    customMessage: `${JSON.stringify(configInfo)}, with error: ${error}`
                }
            });

            return null;
        }
    }

    /**
     * Load a single data connector
     * @param configInfo - data connector config info object
     * @returns {Promise{void}} - the async operation that denotes loading of all the children.
     */
    protected async loadDataConnector(configInfo: ChildExperienceConfigInfo): Promise<void> {

        // Get an experience loader instance for the child experience
        const configId = getConfigId(configInfo);
        if (!configId) {

            // Log an app error if the loader failed to load for whatever reason
            Telemetry.sendAppErrorEvent({
                ...LoadDataConnectorConfigFailed,
                message: "configInfo or Href is null while loading data connector",
                pb: {
                    ...LoadDataConnectorConfigFailed.pb,
                    customMessage: tryStringifyJson(configInfo)
                }
            });

            return;
        }

        const experienceType = getExperienceType(configInfo);

        try {
            const loader = new ExperienceLoader(
                // if it's a config-less experience type, do not pass in the HREF to prevent loading the config.
                // TODO: after deployment, we need to update configs of config-less connectors so that we don't need
                //      a special check like this, and instead the empty HREF can be directly set in the config.
                //      Need to do this in a phased approach so that we don't break PROD users with old scripts.
                experienceType && ConfiglessConnectors[experienceType]
                    ? null
                    : configInfo,
                "",
                "",
                PageBase.getInstance().store,
                this.requestContext,
                experienceType
            );

            // Get the connector for the child experience, and add it to the child connector map
            await loader.loadConnector(true);
        } catch (error) {
            // Log an app error if the loader failed to load for whatever reason
            /// NOTE: DO NOT throw Error or reject promise here, or else the parent might not load the remaining child experiences.
            Telemetry.sendAppErrorEvent(createError(
                LoadDataConnectorFailed,
                `Data connector loading failed for ${experienceType || ""}`,
                `${tryStringifyJson(configInfo)}, with error: ${error}`
            ));
        }
    }

    /**
     * Select and return the proper inner state for the component. The base DataConnector manages the wrapping of the state
     * and so it's getCurrentState() method is used to get the inner state (TState) for the connected component
     * NOTE: The signature of this component requires a state parameter, so that it can be passed into the subspaced()
     * method that is used to connect the component to state
     * @param state For this implementation, this parameter is ignored, since the actual state is managed by the base DataConnector
     */
    private selectComponentState(state: TParentState): TState {
        return (this.getCurrentState());
    }

    /**
     * Start lifecycle of a child experience
     * @param configInfo - the child experience config info
     * @param reRenderCallback - Render callback to call when dependencies are resolved
     * @param childConnectorProps - child experience connector props
     * @returns {Promise{void}} - the async operation that denotes loading of the child.
     */
    private async startProcessingChildExperience(
        configInfo: ChildExperienceConfigInfo,
        reRenderCallback: () => void,
        childConnectorProps?: ExperienceInitialProps
    ): Promise<void> {

        const milestoneDependencyToLoadChildExp = getExperienceLoadDelegate(getExperienceType(configInfo));

        if (milestoneDependencyToLoadChildExp && milestoneDependencyToLoadChildExp.shouldResolve) {
            // Don't load delayed children on server side to avoid HTML mismatches
            if (canUseDOM()) {
                let addTimeout = true;

                if (milestoneDependencyToLoadChildExp.deferralType === DeferralEventType.CustomEventBasedDeferral) {
                    addTimeout = false;
                    configInfo.state = ExperienceState.Unknown;
                }

                this.resolveDependency(
                    configInfo,
                    milestoneDependencyToLoadChildExp.shouldResolve,
                    () => this.loadAndRenderExperience(configInfo, reRenderCallback, true, childConnectorProps),
                    addTimeout
                );
            }
        } else {
            await this.loadAndRenderExperience(configInfo, reRenderCallback, false, childConnectorProps);
        }
    }

    /**
     * load and render experience
     * @param childExpInfo - the child experience config info
     * @param reRenderCallback - Render callback to call when dependencies are resolved
     * @param delayLoaded - whether this experience is delay loaded or not
     * @param childConnectorProps - child experience connector props
     * @returns {Promise{void}} - the async operation that denotes loading of the child.
     */
    private async loadAndRenderExperience(
        childExpInfo: ChildExperienceConfigInfo,
        reRenderCallback: () => void,
        delayLoaded: boolean,
        childConnectorProps?: ExperienceInitialProps
    ): Promise<void> {

        const experienceType = getExperienceType(childExpInfo);
        const configId = getConfigId(childExpInfo);

        // Get an experience loader instance for the child experience
        if (!childExpInfo
            || (!configId && !ConfiglessConnectors[experienceType])
            || !childExpInfo.instanceId) {

            // Log an app error if the loader failed to load for whatever reason
            Telemetry.sendAppErrorEvent({
                ...ChildWithInvalidExperience,
                message: `Attempted to load child with invalid experience ${experienceType || ""}`,
                pb: {
                    ...ChildWithInvalidExperience.pb,
                    customMessage: tryStringifyJson(childExpInfo)
                }
            });

            return;
        }

        try {
            const connector = await this.getChildExperienceConnector(childExpInfo, childConnectorProps);

            if (!connector) {
                childExpInfo.state = ExperienceState.Failed;
                return;
            }

            const milestoneDependencyToRenderChildExp = getExperienceRenderDelegate(experienceType);

            if (milestoneDependencyToRenderChildExp && milestoneDependencyToRenderChildExp.shouldResolve) {
                this.resolveDependency(
                    childExpInfo,
                    milestoneDependencyToRenderChildExp.shouldResolve, () => {
                        childExpInfo.state = ExperienceState.ReadyToRender;
                        this.addConnectorToChildMap(childExpInfo, connector);
                        reRenderCallback();
                    },
                    true
                );
            } else {
                childExpInfo.state = ExperienceState.ReadyToRender;
                this.addConnectorToChildMap(childExpInfo, connector);
                if (delayLoaded) {
                    reRenderCallback();
                }
            }
        } catch (error) {
            // On server side we need to rethrow error so client side rendering takes over, this is to prevent rendering a mismatched page
            if (!canUseDOM()) {
                throw error;
            }

            childExpInfo.state = ExperienceState.Failed;

            // Log an app error if the loader failed to delayload
            Telemetry.sendAppErrorEvent(
                createError(
                    ComponentConnectorAppErrors.ChildDelayLoadError,
                    `Attempted to delay load child ${experienceType} and failed as associated deferral promise returned an error`,
                    stringifyError(error)
                )
            );
        }
    }

    /**
     * add connector to child map
     * @param configInfo - the child experience config info
     * @param childConnector - child experience connector
     */
    private addConnectorToChildMap(configInfo: ChildExperienceConfigInfo, childConnector: IComponentConnector<any, any, any>): void {
        // If the experience loaded, and we got the connected component, then add it to the child map,
        // as long as we have not seen this child before
        if (!this.childMap.has(configInfo.instanceId)) {
            this.childMap.set(configInfo.instanceId, childConnector);
        } else {
            // Log an app error
            Telemetry.sendAppErrorEvent({
                ...ComponentConnectorAppErrors.DuplicateChildInstanceFound,
                message: `Duplicate child instance found: ${childConnector.namespace}`,
                pb: {
                    ...ComponentConnectorAppErrors.DuplicateChildInstanceFound.pb,
                    customMessage: `href: ${getLocationHref()}, parentNamespace: ${this.fullNamespace}`
                }
            });
        }
    }

    /**
     * Check if delay loaded experiences are ready to be rendered
     * @param children - the child experience config info array
     */
    private readyExperienceToRender(children: ChildExperienceConfigInfo[]): void {
        const config = this.config as BaseExperienceConfig<any>;

        if (!config.childExperiencesRenderType || config.childExperiencesRenderType === ChildExperiencesDeferRenderType.batch) {
            for (const child of children) {
                if (child.state !== ExperienceState.ReadyToRender && child.state !== ExperienceState.Failed && child.state !== ExperienceState.Unknown) {
                    return;
                }
            }
        }

        this.reRenderExperience();
    }

    /**
     * Resolves promise dependency with applicable timeout strategy
     * @param dependency - Delegate Promise to resolve dependency
     * @param callback - callback function to call when dependency is resolved
     */
    private resolveDependency(
        configInfo: ChildExperienceConfigInfo,
        dependency: () => Promise<boolean>,
        callback: () => void,
        addTimeout: boolean
    ): void {

        let pending = true;
        let timer: NodeJS.Timer = null;

        if (addTimeout) {
            timer = setTimeout(
                () => {
                    if (pending) {
                        pending = false;
                        callback();
                    }
                },
                this.$maxWaitToBatchRenderChildExperiences
            );
        }

        dependency().then(() => {
            if (addTimeout) {
                clearTimeout(timer);
            }

            if (pending) {
                pending = false;
                callback();
            }
        });
    }

    /**
     * mock experience config through url override
     * @param experienceConfig - experience config
     */
    private mockStalenessConfig(experienceConfig: BaseExperienceConfig<any>) {

        const searchParams = new URLSearchParams(getLocationSearch());
        const enableMockStalenessConfig = searchParams.get("debugstalerevalidation");

        if (enableMockStalenessConfig) {
            if (this.experienceType === ExperienceIds.moneyCard) {
                experienceConfig.stalenessInfo = { isStaleCheckRequired: true, staleThreshold: 3 };
            }
            if (this.experienceType === ExperienceIds.weatherCard) {
                experienceConfig.stalenessInfo = { isStaleCheckRequired: true, staleThreshold: 2 };
            }
            if (this.experienceType === ExperienceIds.river) {
                experienceConfig.stalenessInfo = { isStaleCheckRequired: true, staleThreshold: 1 };
            }
            if (this.experienceType === ExperienceIds.topSitesEdgeNext) {
                experienceConfig.stalenessInfo = { isStaleCheckRequired: true };
            }
            if (this.experienceType === ExperienceIds.backgroundImageEdgeNext) {
                experienceConfig.stalenessInfo = { isStaleCheckRequired: true };
            }
            if (this.experienceType === ExperienceIds.searchBoxEdgeNext) {
                experienceConfig.stalenessInfo = { isStaleCheckRequired: true };
            }
            if (this.experienceType === ExperienceIds.headerEdgeNext) {
                experienceConfig.stalenessInfo = { isStaleCheckRequired: true };
            }
        }
    }
}
